import React, { useEffect, useState, useCallback, useMemo } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import axios from "axios";
import { useNavigate } from 'react-router-dom';

// Constants
const VIEW_TYPES = {
  TOPUP: 'topup',
  RENEW: 'renew',
  MANUAL: 'manual'
};

const MANUAL_ACTIVATION_PARTNER_CODE = '112722282';

const STATUS = {
  INACTIVE: 'InActive',
  RECHARGE: 'Recharge'
};

const FLAGS = {
  RENEW: 1,
  MANUAL: 2
};

const SuperadminRenewal = () => {
  const [data, setData] = useState([]);
  const [renewData, setRenewData] = useState([]);
  const [manualData, setManualData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState(VIEW_TYPES.TOPUP);
  const [actionTaken, setActionTaken] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  // Memoized token and partner code
  const authToken = useMemo(() => localStorage.getItem("token"), []);
  const partnerCode = useMemo(() => localStorage.getItem("partnerCode"), []);

  // Fetch Top-up History
  const fetchTopupHistory = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiUrl}ESIM/Get_topup_salereport?flag=1&partnercode=${partnerCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      
      const result = await response.json();
      
      if (result.isSuccess) {
        setData(result.gettopup_sale || []);
      }
    } catch (err) {
      console.error("Error fetching top-up data:", err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, partnerCode, authToken]);

  // Generic Renew Data Fetcher
  const fetchRenewDataGeneric = useCallback(async (partnerCodeParam, filterOutManual = false) => {
    try {
      const response = await axios.post(
        `${apiUrl}Renew/GetRenewData`,
        {
          partnerCode: partnerCodeParam,
          pageSize: 800,
          pageIndex: 1,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      if (response.data?.isSuccess) {
        let dataToSet = response.data.renewData || [];
        
        // Filter out manual activation partner code if needed
        if (filterOutManual) {
          dataToSet = dataToSet.filter(
            item => item.partnerCode !== MANUAL_ACTIVATION_PARTNER_CODE
          );
        }
        
        return dataToSet;
      }
      
      return [];
    } catch (error) {
      console.error("Error fetching renew data:", error);
      return [];
    }
  }, [apiUrl, authToken]);

  // Fetch Regular Renew Data (filtered)
  const fetchRenewData = useCallback(async () => {
    const data = await fetchRenewDataGeneric('string', true);
    setRenewData(data);
    setLoading(false);
  }, [fetchRenewDataGeneric]);

  // Fetch Manual Activation Data
  const fetchManualActivationData = useCallback(async () => {
    const data = await fetchRenewDataGeneric(MANUAL_ACTIVATION_PARTNER_CODE, false);
    setManualData(data);
    setLoading(false);
  }, [fetchRenewDataGeneric]);

  // Effect to fetch data based on current view
  useEffect(() => {
    setLoading(true);
    
    switch (currentView) {
      case VIEW_TYPES.TOPUP:
        fetchTopupHistory();
        break;
      case VIEW_TYPES.RENEW:
        fetchRenewData();
        break;
      case VIEW_TYPES.MANUAL:
        fetchManualActivationData();
        break;
      default:
        setLoading(false);
    }
  }, [currentView, actionTaken, fetchTopupHistory, fetchRenewData, fetchManualActivationData]);

  // View Toggle Handlers
  const handleViewChange = useCallback((viewType) => {
    setCurrentView(viewType);
  }, []);

  // Status Update Handler
  const handleStatusUpdate = useCallback(async () => {
    setModalLoading(true);

    // Determine flag based on current view
    const flag = currentView === VIEW_TYPES.MANUAL ? FLAGS.MANUAL : FLAGS.RENEW;

    const payload = {
      orderID: selectedOrder.orderID,
      status: STATUS.RECHARGE,
      flag,
    };

    try {
      const response = await axios.post(
        `${apiUrl}Renew/UpdateRenewStatus`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setActionTaken(prev => !prev);
        setModalVisible(false);
        // Optional: Show success message
        alert('Status updated successfully!');
      } else {
        alert("Failed to update status.");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert(error.response?.data?.message || "Error occurred while updating status.");
    } finally {
      setModalLoading(false);
    }
  }, [apiUrl, authToken, currentView, selectedOrder]);

  // CSV Download Utility
  const downloadCSV = useCallback((dataToDownload, filename) => {
    if (!dataToDownload || dataToDownload.length === 0) {
      alert("No data available to download");
      return;
    }

    const headers = Object.keys(dataToDownload[0]);
    const csvRows = [headers.join(',')];
    
    dataToDownload.forEach(item => {
      const row = headers.map(header => {
        const value = item[header] ?? '';
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      });
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // CSV Download Handlers
  const handleDownloadCSV = useCallback(() => {
    const timestamp = new Date().toISOString().split('T')[0];
    let dataToDownload, prefix;

    switch (currentView) {
      case VIEW_TYPES.TOPUP:
        dataToDownload = data;
        prefix = 'topup';
        break;
      case VIEW_TYPES.RENEW:
        dataToDownload = renewData;
        prefix = 'renew';
        break;
      case VIEW_TYPES.MANUAL:
        dataToDownload = manualData;
        prefix = 'manual_activation';
        break;
      default:
        return;
    }

    downloadCSV(dataToDownload, `${prefix}_data_${timestamp}.csv`);
  }, [currentView, data, renewData, manualData, downloadCSV]);

  // Modal Handlers
  const handleOpenModal = useCallback((order) => {
    setSelectedOrder({
      orderID: order.orderID,
      status: order.status === STATUS.RECHARGE ? STATUS.INACTIVE : STATUS.RECHARGE,
    });
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedOrder(null);
  }, []);

  // View Title and Button Labels
  const viewConfig = useMemo(() => ({
    [VIEW_TYPES.TOPUP]: {
      title: "Top-Up Plans",
      hasData: data.length > 0
    },
    [VIEW_TYPES.RENEW]: {
      title: "Renew Data Table",
      hasData: renewData.length > 0
    },
    [VIEW_TYPES.MANUAL]: {
      title: "Manual Activation Data",
      hasData: manualData.length > 0
    }
  }), [data.length, renewData.length, manualData.length]);

  // Render Action Button
  const renderActionButton = useCallback((item) => {
    const isInactive = item.status === STATUS.INACTIVE;
    
    return (
      <button
        onClick={() => handleOpenModal(item)}
        className={`px-4 py-1 rounded text-white transition ${
          isInactive ? "bg-red-500 hover:bg-red-600" : "bg-green-400 hover:bg-green-500"
        }`}
      >
        {isInactive ? "Action Pending" : "Renewed"}
      </button>
    );
  }, [handleOpenModal]);

  // Render Renew/Manual Table
  const renderDataTable = useCallback((tableData) => (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 text-sm">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-2 border">Order ID</th>
            <th className="p-2 border">Partner Code</th>
            <th className="p-2 border">ICCID</th>
            <th className="p-2 border">Plan Name</th>
            <th className="p-2 border">Renewal Days</th>
            <th className="p-2 border">Buy Amount</th>
            <th className="p-2 border">User Amount</th>
           <th className="p-2 border">Purchase Date</th>
          <th className="p-2 border">Request Date</th>
            <th className="p-2 border">New Expiry</th>
            <th className="p-2 border">Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData.length > 0 ? (
            tableData.map((item) => (
              <tr key={item.id || item.orderID} className="hover:bg-gray-50">
                <td className="p-2 border">{item.orderID}</td>
                <td className="p-2 border">{item.partnerCode}</td>
                <td className="p-2 border">{item.iccid}</td>
                <td className="p-2 border">{item.planName}</td>
                <td className="p-2 border">{item.renewalDays} Days</td>
                <td className="p-2 border">${parseFloat(item.buyAmount).toFixed(2)}</td>
                <td className="p-2 border">${parseFloat(item.userAmount).toFixed(2)}</td>
                <td>{new Date(item.purchaseDate).toLocaleDateString()}</td>
                 <td className="p-2 border">
                  {new Date(item.createdDate).toLocaleDateString()}
                </td>
                <td className="p-2 border">
                  {new Date(item.newExpiryDate).toLocaleDateString()}
                </td>
               
                <td className="p-2 border">
                  {renderActionButton(item)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="p-2 border text-center text-gray-500" colSpan="10">
                No data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  ), [renderActionButton]);

  // Render Top-Up Table
  const renderTopupTable = useCallback(() => (
    <table className="w-full bg-white border rounded-lg shadow text-sm">
      <thead className="bg-gray-100">
        <tr className="border-b">
          <th className="text-center p-3 px-5">Plan Name</th>
          <th className="text-center p-3 px-5">Created Date</th>
          <th className="text-center p-3 px-5">Price</th>
          <th className="text-center p-3 px-5">Email</th>
          <th className="text-center p-3 px-5">ICCID</th>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan="5" className="text-center py-4 text-gray-500">
              No top-up plans available.
            </td>
          </tr>
        ) : (
          data.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="text-center p-3 px-5">{item.planname}</td>
              <td className="text-center p-3 px-5">{item.createddate}</td>
              <td className="text-center p-3 px-5">${item.price}.00</td>
              <td className="text-center p-3 px-5">{item.email}</td>
              <td className="text-center p-3 px-5">{item.iccid?.trim()}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  ), [data]);

  return (
    <div className="p-6 overflow-x-auto relative">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold">
          {viewConfig[currentView]?.title}
        </h1>
        
        <div className="flex gap-2 flex-wrap">
          {/* Download CSV Button */}
          <button
            onClick={handleDownloadCSV}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !viewConfig[currentView]?.hasData}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download CSV
          </button>

          {/* View Toggle Buttons */}
          <button
            onClick={() => handleViewChange(VIEW_TYPES.TOPUP)}
            className={`px-4 py-2 rounded shadow transition ${
              currentView === VIEW_TYPES.TOPUP
                ? "bg-blue-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            Top-Up History
          </button>

          <button
            onClick={() => handleViewChange(VIEW_TYPES.RENEW)}
            className={`px-4 py-2 rounded shadow transition ${
              currentView === VIEW_TYPES.RENEW
                ? "bg-blue-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            Renew Data
          </button>

          <button
            onClick={() => handleViewChange(VIEW_TYPES.MANUAL)}
            className={`px-4 py-2 rounded shadow transition ${
              currentView === VIEW_TYPES.MANUAL
                ? "bg-blue-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            Manual Activation
          </button>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {currentView === VIEW_TYPES.TOPUP && renderTopupTable()}
          {currentView === VIEW_TYPES.RENEW && renderDataTable(renewData)}
          {currentView === VIEW_TYPES.MANUAL && renderDataTable(manualData)}
        </>
      )}

      {/* Modal */}
      {modalVisible && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96 relative">
            <h2 className="text-lg font-semibold mb-4">
              {currentView === VIEW_TYPES.MANUAL ? "Update Manual Activation Status" : "Update Renewal Status"}
            </h2>
            <p className="mb-2">
              Order ID: <strong>{selectedOrder.orderID}</strong>
            </p>
            <p className="mb-4 text-sm text-gray-600">
              Flag: <strong>{currentView === VIEW_TYPES.MANUAL ? FLAGS.MANUAL : FLAGS.RENEW}</strong>
            </p>
            <div className="flex justify-between mt-6 gap-2">
              <button
                onClick={handleStatusUpdate}
                disabled={modalLoading}
                className="px-4 py-2 rounded text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                {modalLoading ? "Updating..." : "Confirm Update"}
              </button>
              <button
                onClick={handleCloseModal}
                disabled={modalLoading}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400 text-black disabled:opacity-50 flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperadminRenewal;
